import IconButton from '@/components/ui/custom/buttons/IconButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import SpanInput from '@/components/ui/custom/dataInput/SpanInput';
import Label, { LabelProps } from '@/components/ui/custom/secondary/Label';
import { heading3 } from '@/styles/styles';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import { PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { Control, Path, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { twJoin, twMerge } from 'tailwind-merge';

export type TitleFormProps<T extends Record<string, unknown>> = {
  control: Control<T>;
  fieldName: Path<T>;
  onClose?: () => void;
  prefix?: string;
  className?: Pick<ClassNameComponent, 'span' | 'icon'>;
  label?: LabelProps;
  maxLength?: number;
};

const TitleForm = <T extends Record<string, any>>({
  control,
  prefix,
  label,
  fieldName,
  onClose,
  maxLength,
  className,
}: TitleFormProps<T>) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'section.form',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const {
    field: titleField,
    fieldState: { error },
  } = useController({
    control,
    name: fieldName,
    rules: {
      required: t('title.required'),
    },
  });
  const [editingTitle, setEditingTitle] = useState(false);

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-2">
        {label && <Label label={label} />}
        <SpanInput
          {...titleField}
          className={{
            input: 'w-full',
            prefix: twJoin(heading3, 'text-primary w-fit'),
          }}
          isEditing={editingTitle}
          prefix={prefix}
          maxLenght={maxLength}
          errorLabelText={error?.message}
          onKeyDown={event => {
            if (error?.message) return;
            if (event.key === 'Enter') {
              setEditingTitle(false);
            }
            event.stopPropagation();
          }}
        >
          {value => <h3 className={className?.span}>{value}</h3>}
        </SpanInput>
      </div>
      <ConditionalRenderer condition={!editingTitle}>
        <IconButton
          type="button"
          testId="editTitle"
          tooltip={tCommon('edit')}
          icon={
            <PencilIcon
              className={twMerge('text-primary w-4 h-4', className?.icon)}
            />
          }
          onClick={() => setEditingTitle(prev => !prev)}
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={editingTitle}>
        <IconButton
          type="button"
          testId="editTitle"
          disabled={!!error?.message}
          icon={<XMarkIcon className="text-error w-4 h-4" />}
          onClick={() => {
            setEditingTitle(prev => !prev);
            onClose?.();
          }}
        />
      </ConditionalRenderer>
    </div>
  );
};

export default TitleForm;
